import { useMemo } from 'react';
import { useCheckPermission } from './useUserRoles';
import { useItemCategories } from './useItemCategories';
import type { ItemCategory } from '@/types/item';
import { logger } from '@/lib/logger';

// Hook to check base Items access
export const useItemsAccess = () => {
  return useCheckPermission('ITEMS_ACCESS');
};

// Hook to check column visibility permissions
export const useItemsColumnPermissions = () => {
  const canViewProject = useCheckPermission('ITEMS_VIEW_PROJECT_COLUMN');
  const canViewItem = useCheckPermission('ITEMS_VIEW_ITEM_COLUMN');
  const canViewCategory = useCheckPermission('ITEMS_VIEW_CATEGORY_COLUMN');
  const canViewShop = useCheckPermission('ITEMS_VIEW_SHOP_COLUMN');
  const canViewSpecs = useCheckPermission('ITEMS_VIEW_SPECS_COLUMN');
  const canViewPo = useCheckPermission('ITEMS_VIEW_PO_COLUMN');
  const canViewContract = useCheckPermission('ITEMS_VIEW_CONTRACT_COLUMN');
  const canViewTimeline = useCheckPermission('ITEMS_VIEW_TIMELINE_COLUMN');

  return useMemo(() => ({
    project: canViewProject.data || false,
    item: canViewItem.data || false,
    category: canViewCategory.data || false,
    shop: canViewShop.data || false,
    specs: canViewSpecs.data || false,
    po: canViewPo.data || false,
    contract: canViewContract.data || false,
    timeline: canViewTimeline.data || false,
    isLoading: canViewProject.isLoading || canViewItem.isLoading || canViewCategory.isLoading || 
               canViewShop.isLoading || canViewSpecs.isLoading || canViewPo.isLoading || 
               canViewContract.isLoading || canViewTimeline.isLoading
  }), [
    canViewProject.data, canViewItem.data, canViewCategory.data, canViewShop.data,
    canViewSpecs.data, canViewPo.data, canViewContract.data, canViewTimeline.data,
    canViewProject.isLoading, canViewItem.isLoading, canViewCategory.isLoading,
    canViewShop.isLoading, canViewSpecs.isLoading, canViewPo.isLoading,
    canViewContract.isLoading, canViewTimeline.isLoading
  ]);
};

// Hook to check category access permissions - Dynamic version using database categories
export const useItemsCategoryPermissions = () => {
  const { data: categories, isLoading: categoriesLoading } = useItemCategories();
  const canViewCategoryColumn = useCheckPermission('ITEMS_VIEW_CATEGORY_COLUMN');
  const hasItemsAccess = useCheckPermission('ITEMS_ACCESS');
  const hasItemsViewInProject = useCheckPermission('ITEMS_VIEW_IN_PROJECT');
  const hasViewProjects = useCheckPermission('VIEW_PROJECTS');

  const categoryPermissions = useMemo(() => {
    if (!categories) return {};
    
    const permissions: Record<string, boolean> = {};
    // ✅ PHASE 1: Use AND logic - Global permissions required
    // Note: Project-level check happens in useProjectPermissions hook
    const hasBasicAccess = (hasItemsAccess.data || hasItemsViewInProject.data || hasViewProjects.data) || false;
    
    categories.forEach(category => {
      permissions[category.name] = hasBasicAccess;
    });
    
    return permissions;
  }, [categories, hasItemsAccess.data, hasItemsViewInProject.data, hasViewProjects.data]);

  const isLoading = categoriesLoading || hasItemsAccess.isLoading || hasItemsViewInProject.isLoading || hasViewProjects.isLoading || canViewCategoryColumn.isLoading;

  // Compute basic access once for fallback logic
  const hasBasicAccessNow = Boolean(hasItemsAccess.data || hasItemsViewInProject.data || hasViewProjects.data);

  // Helper function to check if user can view a specific category
  // Permissive during loading; fallback to basic access for unknown categories
  const canViewCategoryFunc = (category: ItemCategory): boolean => {
    if (isLoading) return true;
    if (Object.prototype.hasOwnProperty.call(categoryPermissions, category)) {
      return categoryPermissions[category] || false;
    }
    return hasBasicAccessNow;
  };
  // Get list of allowed categories
  const allowedCategories = useMemo(() => {
    const allowed = Object.entries(categoryPermissions)
      .filter(([_, canView]) => canView)
      .map(([category, _]) => category as ItemCategory);
    
    // Debug logging
    logger.debug('useItemsCategoryPermissions - allowedCategories:', allowed);
    logger.debug('useItemsCategoryPermissions - hasItemsAccess:', hasItemsAccess.data);
    logger.debug('useItemsCategoryPermissions - hasItemsViewInProject:', hasItemsViewInProject.data);
    logger.debug('useItemsCategoryPermissions - hasViewProjects:', hasViewProjects.data);
    
    return allowed;
  }, [categoryPermissions, hasItemsAccess.data, hasItemsViewInProject.data, hasViewProjects.data]);

  return {
    categoryPermissions,
    canViewCategory: canViewCategoryFunc,
    allowedCategories,
    showCategoryColumn: canViewCategoryColumn.data || false, // Separate UI control
    isLoading
  };
};

// Hook to check action permissions
export const useItemsActionPermissions = () => {
  const canEditBasic = useCheckPermission('ITEMS_EDIT_BASIC');
  const canEditFunctions = useCheckPermission('ITEMS_EDIT_FUNCTIONS');

  return useMemo(() => ({
    canEditBasic: canEditBasic.data || false,
    canEditFunctions: canEditFunctions.data || false,
    isLoading: canEditBasic.isLoading || canEditFunctions.isLoading
  }), [
    canEditBasic.data, canEditFunctions.data,
    canEditBasic.isLoading, canEditFunctions.isLoading
  ]);
};